import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Avatar, EmptyState, ErrorState, Field, Modal, Pagination, Spinner, StatusBadge } from '../../components/ui.jsx';
import { api, toQuery } from '../../api/client.js';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { useToast } from '../../state/ToastContext.jsx';
import { formatDate, formatXaf, timeAgo } from '../../lib/format.js';

const DECISIONS = {
  under_review: {
    label: 'Mark under review',
    title: 'Mark this application under review',
    lead: 'The applicant is told you are looking at it. Nothing is decided yet.',
    tone: 'outline'
  },
  approved: {
    label: 'Approve',
    title: 'Approve this application',
    lead: 'The house moves to Pending and every other open application on it is rejected automatically. Say what happens next so the tenant knows. You can draw up their lease straight from this page afterwards.',
    tone: 'primary'
  },
  rejected: {
    label: 'Reject',
    title: 'Reject this application',
    lead: 'A short honest reason is kinder than silence, and it saves you a phone call.',
    tone: 'danger'
  }
};

const DecisionModal = ({ application, decision, onClose, onDone }) => {
  const toast = useToast();
  const [note, setNote] = useState(
    decision === 'approved' ? 'Deposit and first month payable before you collect the keys.' : ''
  );
  const [busy, setBusy] = useState(false);
  const copy = DECISIONS[decision];

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/applications/${application.id}/status`, { status: decision, note: note.trim() || undefined });
      toast.success('The applicant has been notified.');
      onDone();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={copy.title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="decision-form" className={`btn btn-${copy.tone}`} disabled={busy}>
            {busy ? 'Saving...' : copy.label}
          </button>
        </>
      }
    >
      <p className="modal-lead">{copy.lead}</p>
      <form id="decision-form" onSubmit={submit}>
        <Field label="Note to the applicant" hint="They see this word for word.">
          <textarea rows="4" value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </form>
    </Modal>
  );
};

const LandlordApplications = () => {
  useTitle('Applications');
  const [params, setParams] = useSearchParams();
  const status = params.get('status') || 'any';
  const propertyId = params.get('propertyId') || '';
  const page = Number(params.get('page') || 1);

  const properties = useFetch('/properties/mine');
  const query = toQuery({ status, propertyId, page, pageSize: 10 });
  const { data, loading, error, reload } = useFetch(`/applications${query}`);

  const [pending, setPending] = useState(null);

  const setParam = (patch) => {
    const next = { status, propertyId, page, ...patch };
    setParams(new URLSearchParams(toQuery({ ...next, page: patch.page ?? 1 }).replace(/^\?/, '')));
  };

  const items = data?.items || [];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Applications</h1>
          <p className="muted">Work through them oldest first. Applicants can see every status change.</p>
        </div>
      </header>

      <div className="filter-bar">
        <label className="inline-select">
          <span>Listing</span>
          <select value={propertyId} onChange={(event) => setParam({ propertyId: event.target.value })}>
            <option value="">All my listings</option>
            {(properties.data?.items || []).map((property) => (
              <option key={property.id} value={property.id}>
                {property.title}
              </option>
            ))}
          </select>
        </label>

        <div className="tab-row">
          {[
            ['any', 'All'],
            ['submitted', 'New'],
            ['under_review', 'Under review'],
            ['approved', 'Approved'],
            ['rejected', 'Rejected'],
            ['withdrawn', 'Withdrawn']
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`tab ${status === value ? 'is-current' : ''}`}
              onClick={() => setParam({ status: value })}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}

      {!loading && items.length === 0 ? (
        <EmptyState
          title="No applications here"
          message={
            status === 'any'
              ? 'Nobody has applied yet. Check that your listings have photos and that the rent is in line with the neighbourhood.'
              : 'Try a different tab.'
          }
        />
      ) : null}

      <div className="applicant-list">
        {items.map((application) => (
          <article key={application.id} className="applicant-card">
            <header>
              <div className="applicant-who">
                <Avatar name={application.tenant?.name} url={application.tenant?.avatarUrl} size={46} />
                <div>
                  <strong>{application.tenant?.name}</strong>
                  <span className="muted">
                    {application.tenant?.phone} - {application.tenant?.email}
                  </span>
                  <span className="muted">Applied {timeAgo(application.createdAt)}</span>
                </div>
              </div>
              <StatusBadge status={application.status} />
            </header>

            <p className="applicant-property">
              For <Link to={`/properties/${application.propertyId}`}>{application.property?.title}</Link> -{' '}
              {formatXaf(application.property?.price)} / month
            </p>

            <dl className="applicant-facts">
              <div>
                <dt>People moving in</dt>
                <dd>{application.occupants}</dd>
              </div>
              <div>
                <dt>Wants to move in</dt>
                <dd>{application.moveInDate ? formatDate(application.moveInDate) : 'Not stated'}</dd>
              </div>
              <div>
                <dt>Monthly income</dt>
                <dd>{application.monthlyIncome ? formatXaf(application.monthlyIncome) : 'Not stated'}</dd>
              </div>
              <div>
                <dt>Rent to income</dt>
                <dd>
                  {application.monthlyIncome
                    ? `${Math.round((application.property.price / application.monthlyIncome) * 100)}% of income`
                    : '-'}
                </dd>
              </div>
              <div>
                <dt>Occupation</dt>
                <dd>{application.employment || 'Not stated'}</dd>
              </div>
            </dl>

            {application.coverMessage ? <blockquote className="applicant-message">{application.coverMessage}</blockquote> : null}

            <div className="applicant-docs">
              <strong>Documents</strong>
              {application.documents?.length ? (
                <ul className="doc-list">
                  {application.documents.map((document) => (
                    <li key={document.id}>
                      <a href={document.url} target="_blank" rel="noreferrer noopener">
                        {document.label || document.kind}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="muted"> none attached</span>
              )}
            </div>

            {application.decisionNote ? (
              <p className="decision-note">
                <strong>Your note:</strong> {application.decisionNote}
              </p>
            ) : null}

            <footer className="applicant-actions">
              {application.status === 'submitted' ? (
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => setPending({ application, decision: 'under_review' })}
                >
                  Mark under review
                </button>
              ) : null}

              {['submitted', 'under_review'].includes(application.status) ? (
                <>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => setPending({ application, decision: 'approved' })}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => setPending({ application, decision: 'rejected' })}
                  >
                    Reject
                  </button>
                </>
              ) : null}

              {application.status === 'approved' ? (
                <>
                  {/* The whole point of approving somebody. The editor opens with this
                      property and this applicant already chosen. */}
                  <Link
                    to={`/landlord/leases/new?applicationId=${application.id}`}
                    className="btn btn-primary btn-sm"
                  >
                    Draw up a lease
                  </Link>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setPending({ application, decision: 'rejected' })}
                  >
                    Cancel this approval
                  </button>
                </>
              ) : null}

              <Link
                to={`/messages?propertyId=${application.propertyId}&withUser=${application.tenantId}`}
                className="btn btn-ghost btn-sm"
              >
                Message
              </Link>
            </footer>
          </article>
        ))}
      </div>

      {data ? <Pagination page={data.page} totalPages={data.totalPages} onChange={(next) => setParam({ page: next })} /> : null}

      {pending ? (
        <DecisionModal
          application={pending.application}
          decision={pending.decision}
          onClose={() => setPending(null)}
          onDone={() => {
            setPending(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
};

export default LandlordApplications;
